import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg, downloadPdf } from "../../lib/api";
import { PageHeader, StatusBadge, RagDot } from "../../components/bits";
import { Button } from "../../components/ui/button";
import { Sparkles, Trash2, FileDown } from "lucide-react";

const SECTIONS = [
  ["progressThisPeriod", "Progress this period"],
  ["plannedForNextPeriod", "Planned for next period"],
  ["keyRisksIssues", "Key risks & issues"],
  ["decisionsSupportNeeded", "Decisions / support needed"],
  ["budgetScheduleNotes", "Budget & schedule notes"],
];

export default function StatusReports() {
  const { projectId } = useParams();
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/projects/${projectId}/artifacts/status_reports`).then((r) =>
      setReports(r.data.sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || "")))
    ).catch((e) => toast.error(errMsg(e)));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/ai/generate`, { generationType: "statusreport" });
      const period = new Date().toISOString().slice(0, 10);
      await api.post(`/projects/${projectId}/artifacts/status_reports`, { ...data.result, reportingPeriod: `AI draft — ${period}` });
      toast.success("AI status report drafted and saved");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div data-testid="status-reports-page">
      <PageHeader overline="Reporting" title="Status Reports"
        actions={
          <Button data-testid="generate-status-report-btn" className="rounded-none bg-[#1E6FE8] hover:bg-[#1557C6]" onClick={generate} disabled={busy}>
            <Sparkles className="h-4 w-4 mr-1" />{busy ? "Drafting…" : "Draft with AI"}
          </Button>
        } />
      <div className="space-y-4 max-w-4xl">
        {reports.length === 0 && <div className="border border-dashed border-border p-10 text-center text-muted-foreground">No status reports yet.</div>}
        {reports.map((r) => (
          <div key={r.id} data-testid="status-report-card" className="border border-border bg-card">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
              <div className="flex items-center gap-3">
                <span className="font-display font-bold tracking-tight">{r.reportingPeriod || "Report"}</span>
                <RagDot value={r.overallStatus} />
              </div>
              <div className="flex items-center">
              <button data-testid="export-status-report-btn" title="Export PDF"
                onClick={async () => {
                  try { await downloadPdf(`/projects/${projectId}/export/status-report/${r.id}`, `StatusReport-${(r.reportingPeriod || "report").replace(/[^a-z0-9]+/gi, "-")}.pdf`); toast.success("PDF downloaded"); }
                  catch (e) { toast.error(errMsg(e)); }
                }}
                className="p-1 hover:text-[#1E6FE8] mr-1"><FileDown className="h-3.5 w-3.5" /></button>
              <button data-testid="delete-status-report-btn" onClick={async () => { await api.delete(`/projects/${projectId}/artifacts/status_reports/${r.id}`); load(); }}
                className="p-1 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 p-4 text-sm">
              {SECTIONS.map(([key, label]) => (
                <div key={key}>
                  <div className="overline-label">{label}</div>
                  <p className="mt-0.5 leading-relaxed">{r[key] || "—"}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
